import {useCopilotSpaceSelection} from '@github-ui/copilot-chat/contexts/CopilotSpaceSelectionContext'
import {useSelectedCopilotSpaceId} from '@github-ui/copilot-chat/hooks/use-selected-custom-copilot-id'
import type {CopilotSpaceId} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {useCallback, useEffect} from 'react'

/**
 * Custom hook to manage assigning a space to a pending (not persisted) chat thread
 *
 * @returns Object with space selection state and handlers
 */
export function useSpaceSelection() {
  const initialCopilotSpaceId = useSelectedCopilotSpaceId()
  const {selectedCopilotSpaceId, setSelectedCopilotSpaceId, selectedThreadId} = useCopilotSpaceSelection()

  const readOnly = !!selectedThreadId || !!initialCopilotSpaceId
  // Initialize context with initial value if provided
  useEffect(() => {
    // Always update to match the thread's space, even if we already have a selection
    setSelectedCopilotSpaceId(initialCopilotSpaceId || undefined)
  }, [initialCopilotSpaceId, setSelectedCopilotSpaceId])

  // Use the context-managed space, falling back to initial prop
  const activeCopilotSpaceId = selectedCopilotSpaceId || initialCopilotSpaceId

  // Handler for when user selects a space from the SpacesSelect
  const handleSpaceSelect = useCallback(
    (space: CopilotSpaceId) => {
      if (!readOnly) {
        setSelectedCopilotSpaceId({id: space.id, owner: space.owner})
      }
    },
    [setSelectedCopilotSpaceId, readOnly],
  )

  // Clear selected space when user wants to reset
  const clearSpaceSelection = useCallback(() => {
    if (!readOnly) {
      setSelectedCopilotSpaceId(undefined)
    }
  }, [setSelectedCopilotSpaceId, readOnly])

  return {
    /** The space id from the URL, the currently selected thread, or from a pending thread in immersive chosen via the SpacesSelect */
    activeCopilotSpaceId,
    /** Spaces can't be assigned to existing threads */
    readOnly,
    handleSpaceSelect,
    clearSpaceSelection,
  }
}
